import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import drEasyApi from '../Api/Customapi';

export const useFacilities = () => {
    const queryClient = useQueryClient();

    // ============= CATEGORIES =============
    
    // Get all categories
    const useGetCategories = () => {
        return useQuery({
            queryKey: ['categories'],
            queryFn: async () => {
                const { data } = await drEasyApi.get('/api/Facilities/GetMyCategories');
                return data;
            },
            onError: (err) => {
                if (err.response?.status === 403) {
                    window.location.href = "/sub";
                } else {
                    message.error('Failed to fetch categories');
                }
            }
        });
    };

    // Create category
    const useCreateCategory = () => {
        return useMutation({
            mutationFn: async (categoryData) => {
                const { data } = await drEasyApi.post('/api/Facilities/AddCategory', {
                    category: categoryData
                });
                return data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['categories'] });
                message.success('Category created successfully');
            },
            onError: (err) => {
                if (err?.response?.status === 400) {
                    const errorMessage = err?.response?.data?.message || 'Please check your input and try again';
                    message.error(errorMessage);
                } else {
                    message.error('Failed to create category');
                }
            }
        });
    };

    // Update category
    const useUpdateCategory = () => {
        return useMutation({
            mutationFn: async ({ id, ...categoryData }) => {
                const { data } = await drEasyApi.put('/api/Facilities/EditCategory', {
                    category: {
                        id,
                        ...categoryData
                    }
                });
                return data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['categories'] });
                message.success('Category updated successfully');
            },
            onError: (err) => {
                if (err?.response?.status === 400) {
                    const errorMessage = err?.response?.data?.message || 'Please check your input and try again';
                    message.error(errorMessage);
                } else {
                    message.error('Failed to update category');
                }
            }
        });
    };

    const useDeleteCategory = () => {
        return useMutation({
            mutationFn: async (id) => {
                const { data } = await drEasyApi.delete(
                    `/api/Facilities/DeleteCategory`,
                    {
                        params: { Id: id }
                    }
                );
                return data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['categories'] });
                queryClient.invalidateQueries({ queryKey: ['facilities'] });
                message.success('Category deleted successfully');
            },
            onError: () => {
                message.error('Failed to delete category');
            }
        });
    };

    // ============= FACILITIES =============

    // Get facilities with pagination and filters
    const useGetFacilities = (filters) => {
        return useQuery({
            queryKey: ['facilities', filters],
            queryFn: async () => {
                const params = new URLSearchParams();
                
                if (filters.PageNumber) params.append('PageNumber', filters.PageNumber);
                if (filters.PageSize) params.append('PageSize', filters.PageSize);
                if (filters.Name) params.append('Name', filters.Name);
                if (filters.CategoryId) params.append('CategoryId', filters.CategoryId);

                const { data } = await drEasyApi.get(`/api/Facilities/GetFacilities?${params.toString()}`);
                return data;
            },
            onError: (err) => {
                if (err.response?.status === 403) {
                    window.location.href = "/sub";
                } else {
                    message.error('Failed to fetch facilities');
                }
            }
        });
    };

    // Get facility by ID
    // Note: API doesn't have a dedicated endpoint, so we fetch all and filter client-side
    const useGetFacilityById = (id) => {
        return useQuery({
            queryKey: ['facility', id],
            queryFn: async () => {
                // Fetch all facilities with a large page size
                const { data } = await drEasyApi.get('/api/Facilities/GetFacilities?PageNumber=1&PageSize=1000');
                
                // Find the facility with matching ID
                const facility = data?.items?.find(item => item.id === id);
                
                if (!facility) {
                    throw new Error('Facility not found');
                }
                
                return facility;
            },
            enabled: !!id,
            onError: (err) => {
                if (err.response?.status === 403) {
                    window.location.href = "/sub";
                } else if (err.message === 'Facility not found') {
                    message.error('Facility not found');
                } else {
                    message.error('Failed to fetch facility details');
                }
            }
        });
    };

    // Create facility - FIXED: Changed to /AddFacility and wrapped data in facility object
    const useCreateFacility = () => {
        return useMutation({
            mutationFn: async (facilityData) => {
                const { data } = await drEasyApi.post('/api/Facilities/AddFacility', {
                    facility: facilityData
                });
                return data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['facilities'] });
                message.success('Facility created successfully');
            },
            onError: (err) => {
                if (err?.response?.status === 400) {
                    const errorMessage = err?.response?.data?.message || 'Please check your input and try again';
                    message.error(errorMessage);
                } else {
                    message.error('Failed to create facility');
                }
            }
        });
    };

    // Update facility - FIXED: Changed to /EditFacility and wrapped data in facility object
    const useUpdateFacility = () => {
        return useMutation({
            mutationFn: async ({ id, ...facilityData }) => {
                const { data } = await drEasyApi.put('/api/Facilities/EditFacility', {
                    facility: {
                        id,
                        ...facilityData
                    }
                });
                return data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['facilities'] });
                queryClient.invalidateQueries({ queryKey: ['facility'] });
                message.success('Facility updated successfully');
            },
            onError: (err) => {
                if (err?.response?.status === 400) {
                    const errorMessage = err?.response?.data?.message || 'Please check your input and try again';
                    message.error(errorMessage);
                } else {
                    message.error('Failed to update facility');
                }
            }
        });
    };

    // Delete facility - FIXED: Changed to use query parameter instead of path parameter
    const useDeleteFacility = () => {
        return useMutation({
            mutationFn: async (id) => {
                const { data } = await drEasyApi.delete('/api/Facilities/DeleteFacility', {
                    params: { Id: id }
                });
                return data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['facilities'] });
                message.success('Facility deleted successfully');
            },
            onError: () => {
                message.error('Failed to delete facility');
            }
        });
    };

    // ============= PRODUCE/CONSUME =============

    // Produce facility (add stock)
    const useProduceFacility = () => {
        return useMutation({
            mutationFn: async (produceData) => {
                const { data } = await drEasyApi.post('/api/Facilities/ProduceFacility', produceData);
                return data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['facilities'] });
                queryClient.invalidateQueries({ queryKey: ['facility'] });
                queryClient.invalidateQueries({ queryKey: ['facilityHistory'] });
                message.success('Stock added successfully');
            },
            onError: (err) => {
                if (err?.response?.status === 400) {
                    const errorMessage = err?.response?.data?.message || 'Please check your input and try again';
                    message.error(errorMessage);
                } else {
                    message.error('Failed to add stock');
                }
            }
        });
    };

    // Consume facility (remove stock)
    const useConsumeFacility = () => {
        return useMutation({
            mutationFn: async (consumeData) => {
                const { data } = await drEasyApi.post('/api/Facilities/ConsumeFacility', consumeData);
                return data;
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['facilities'] });
                queryClient.invalidateQueries({ queryKey: ['facility'] });
                queryClient.invalidateQueries({ queryKey: ['facilityHistory'] });
                message.success('Stock consumed successfully');
            },
            onError: (err) => {
                if (err?.response?.status === 400) {
                    const errorMessage = err?.response?.data?.message || 'Failed to consume stock';
                    message.error(errorMessage);
                } else {
                    message.error('Failed to consume stock');
                }
            }
        });
    };

    // ============= HISTORY =============

    // Get facility history
    const useGetFacilityHistory = (filters) => {
        return useQuery({
            queryKey: ['facilityHistory', filters],
            queryFn: async () => {
                const params = new URLSearchParams();
                
                if (filters.FacilityId) params.append('FacilityId', filters.FacilityId);
                if (filters.PageNumber) params.append('PageNumber', filters.PageNumber);
                if (filters.PageSize) params.append('PageSize', filters.PageSize);
                if (filters.StartDate) params.append('StartDate', filters.StartDate);
                if (filters.EndDate) params.append('EndDate', filters.EndDate);

                const { data } = await drEasyApi.get(`/api/Facilities/GetFacilityHistory?${params.toString()}`);
                return data;
            },
            enabled: !!filters.FacilityId,
            onError: (err) => {
                if (err.response?.status === 403) {
                    window.location.href = "/sub";
                } else {
                    message.error('Failed to fetch facility history');
                }
            }
        });
    };

    return {
        // Categories
        useGetCategories,
        useCreateCategory,
        useUpdateCategory,
        useDeleteCategory,
        
        // Facilities
        useGetFacilities,
        useGetFacilityById,
        useCreateFacility,
        useUpdateFacility,
        useDeleteFacility,
        
        // Produce/Consume
        useProduceFacility,
        useConsumeFacility,
        
        // History
        useGetFacilityHistory,
    };
};


export const useGetSuppliers = () => {
    return useQuery({
        queryKey: ['suppliers'],
        queryFn: async () => {
            const res = await drEasyApi.get('/api/Equipments/GetMySuppliers');
            return res.data || [];
        },
        onError: (err) => {
            if (err.response?.status === 403) {
                window.location.href = "/sub";
            } else {
                message.error('Failed to fetch suppliers');
            }
        }
    });
};